import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, Users, FileText, LayoutDashboard, Activity, Flame,
  Clock, RefreshCw, Loader2, ChevronRight, X, Sparkles, Building2, Mail,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const TABS = [
  { id: 'overview', label: 'Overview', icon: TrendingUp },
  { id: 'peak', label: 'Peak Times', icon: Clock },
  { id: 'content', label: 'Content', icon: FileText },
  { id: 'teams', label: 'Teams', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
];

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-border/40 bg-card/40 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-heading font-semibold text-foreground mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-xl border border-border/40 bg-card/30 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-heading font-semibold text-foreground">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}
function ago(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

// ── Overview ─────────────────────────────────────────────────────────
function OverviewSection({ days }) {
  const [summary, setSummary] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ok = true;
    setLoading(true);
    Promise.all([api.getGovernanceSummary(days), api.getGovernanceAdoption()])
      .then(([s, a]) => { if (ok) { setSummary(s); setAdoption(a); } })
      .catch(() => {})
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [days]);

  if (loading) return <Loading />;
  if (!summary) return <Empty text="No usage data yet." />;

  const events = summary.by_event || {};
  const maxEvent = Math.max(1, ...Object.values(events));
  const funnel = adoption?.funnel || [];
  const funnelMax = Math.max(1, ...funnel.map(f => f.count || 0));

  return (
    <div data-testid="adoption-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi testid="kpi-dau" label="Daily active" value={fmt(summary.dau)} sub="last 24h" />
        <Kpi testid="kpi-wau" label="Weekly active" value={fmt(summary.wau)} sub="last 7d" />
        <Kpi testid="kpi-mau" label="Monthly active" value={fmt(summary.mau)} sub="last 30d" />
        <Kpi testid="kpi-stickiness" label="Stickiness" value={`${summary.stickiness_pct ?? 0}%`} sub="DAU / MAU" />
        <Kpi testid="kpi-seats" label="Seats used" value={`${summary.seat_utilization_pct ?? 0}%`} sub={`${fmt(summary.active_seats_30d)} / ${fmt(summary.total_seats)}`} />
        <Kpi testid="kpi-events" label="Events" value={fmt(Object.values(events).reduce((a, b) => a + b, 0))} sub={`last ${days}d`} />
      </div>

      <Section title="Adoption funnel">
        <div className="space-y-2" data-testid="adoption-funnel">
          {funnel.map((f) => (
            <div key={f.stage} className="flex items-center gap-3">
              <div className="w-32 text-xs text-muted-foreground shrink-0">{f.stage}</div>
              <div className="flex-1 h-6 rounded-md bg-muted/30 overflow-hidden">
                <div className="h-full bg-primary/60 rounded-md transition-all duration-500"
                  style={{ width: `${Math.max(2, (f.count / funnelMax) * 100)}%` }} />
              </div>
              <div className="w-12 text-right text-xs font-medium text-foreground">{fmt(f.count)}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="What people are doing">
        <div className="space-y-1.5" data-testid="event-breakdown">
          {Object.entries(events).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <div className="w-24 text-xs text-muted-foreground capitalize shrink-0">{k.replace('_', ' ')}</div>
              <div className="flex-1 h-4 rounded bg-muted/30 overflow-hidden">
                <div className="h-full bg-emerald-500/50 rounded" style={{ width: `${(v / maxEvent) * 100}%` }} />
              </div>
              <div className="w-14 text-right text-xs text-foreground">{fmt(v)}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ── Peak Times ───────────────────────────────────────────────────────
function PeakSection({ days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null); // {d,h,count,x,y}
  const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();
  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.getPeakUsage(days, tz).then(d => ok && setData(d)).catch(() => {}).finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [days, tz]);

  if (loading) return <Loading />;
  if (!data || !data.total_events) return <Empty text="No activity recorded in this window yet." />;

  const cells = data.cells || [];
  const dayLabels = data.day_labels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const max = Math.max(1, ...cells.flat());
  const peak = data.peak || {};
  const tzLabel = (data.timezone || tz || 'UTC').replace(/_/g, ' ');
  const hourLabel = (h) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);
  const hourFull = (h) => (h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`);

  return (
    <div data-testid="adoption-peak">
      <Section title="Busiest times" right={<span className="text-[11px] text-muted-foreground">Your local time ({tzLabel}) · Peak: {dayLabels[peak.dow] ?? '—'} {peak.hour != null ? hourLabel(peak.hour) : ''} · {fmt(peak.count)} events</span>}>
        <div className="relative overflow-x-auto" onMouseLeave={() => setHover(null)}>
          <div className="min-w-[720px]">
            <div className="flex">
              <div className="w-10 shrink-0" />
              {Array.from({ length: 24 }).map((_, h) => (
                <div key={h} className="flex-1 text-center text-[8px] text-muted-foreground/70">{h % 3 === 0 ? hourLabel(h) : ''}</div>
              ))}
            </div>
            {cells.map((row, d) => (
              <div key={d} className="flex items-center">
                <div className="w-10 shrink-0 text-[10px] text-muted-foreground">{dayLabels[d]}</div>
                {row.map((c, h) => {
                  const intensity = c / max;
                  return (
                    <div key={h} className="flex-1 aspect-square m-[1px] rounded-sm cursor-pointer transition-transform hover:scale-125 hover:ring-1 hover:ring-primary/60"
                      onMouseEnter={(e) => setHover({ d, h, count: c, x: e.clientX, y: e.clientY })}
                      onMouseMove={(e) => setHover({ d, h, count: c, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setHover(null)}
                      style={{ backgroundColor: c === 0 ? 'rgba(148,163,184,0.06)' : `rgba(16,185,129,${0.15 + intensity * 0.75})` }}
                      data-testid={`heat-${d}-${h}`} />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 text-[10px] text-muted-foreground">
          <span>Less</span>
          {[0.15, 0.4, 0.65, 0.9].map((o, i) => (
            <div key={i} className="w-4 h-3 rounded-sm" style={{ backgroundColor: `rgba(16,185,129,${o})` }} />
          ))}
          <span>More</span>
        </div>
      </Section>
      {hover && (
        <div className="fixed z-[130] pointer-events-none px-2.5 py-1.5 rounded-lg bg-popover border border-border/60 shadow-xl text-xs"
          style={{ left: Math.min(hover.x + 14, window.innerWidth - 180), top: hover.y + 14 }}
          data-testid="heat-tooltip">
          <div className="font-medium text-foreground">{dayLabels[hover.d]} · {hourFull(hover.h)}</div>
          <div className="text-muted-foreground">{fmt(hover.count)} event{hover.count === 1 ? '' : 's'}</div>
        </div>
      )}
    </div>
  );
}

// ── Content ──────────────────────────────────────────────────────────
function ContentSection() {
  const [type, setType] = useState('report');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.getContentUsage().then(d => ok && setData(d)).catch(() => {}).finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, []);

  if (loading) return <Loading />;
  const block = (data || {})[type] || { most_used: [], least_used: [], never_used: [], total: 0, never_used_count: 0 };
  const openAsset = (r) => {
    if (type === 'report') navigate(`/reports/view?id=${r.object_id}`);
    else navigate(`/dashboards/view?id=${r.object_id}`);
  };

  const Row = ({ r }) => (
    <button onClick={() => openAsset(r)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors text-left" data-testid={`content-row-${r.object_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground truncate">{r.object_name}</div>
        <div className="text-[10px] text-muted-foreground">last viewed {ago(r.last_accessed_at)}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-medium text-foreground">{fmt(r.view_count)}</div>
        <div className="text-[10px] text-muted-foreground">{fmt(r.unique_viewer_count)} viewers</div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
    </button>
  );

  return (
    <div data-testid="adoption-content">
      <div className="flex items-center gap-2 mb-4">
        {['report', 'dashboard'].map(t => (
          <button key={t} onClick={() => setType(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${type === t ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
            data-testid={`content-type-${t}`}>
            {t === 'report' ? <FileText className="w-3.5 h-3.5" /> : <LayoutDashboard className="w-3.5 h-3.5" />}
            {t === 'report' ? 'Reports' : 'Dashboards'}
          </button>
        ))}
        <span className="text-[11px] text-muted-foreground ml-auto">{fmt(block.total)} total · {fmt(block.never_used_count)} never used</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Most used">
          {block.most_used.length ? block.most_used.map(r => <Row key={r.object_id} r={r} />) : <Empty text="No views recorded yet." />}
        </Section>
        <Section title="Least used">
          {block.least_used.length ? block.least_used.map(r => <Row key={r.object_id} r={r} />) : <Empty text="No views recorded yet." />}
        </Section>
      </div>

      <Section title={`Never used (${fmt(block.never_used_count)})`}>
        {block.never_used.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1" data-testid="never-used-list">
            {block.never_used.map(r => (
              <button key={r.object_id} onClick={() => openAsset(r)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors text-left" data-testid={`never-used-${r.object_id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                <span className="text-sm text-foreground truncate flex-1">{r.object_name}</span>
                <span className="text-[10px] text-muted-foreground">created {ago(r.created_at)}</span>
              </button>
            ))}
          </div>
        ) : <Empty text="Everything is being used — nothing sitting idle." />}
      </Section>
    </div>
  );
}

// ── Teams ────────────────────────────────────────────────────────────
function TeamsSection({ days }) {
  const [dim, setDim] = useState('department');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.getTeamRollup(dim, days).then(d => ok && setData(d)).catch(() => {}).finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [dim, days]);

  if (loading) return <Loading />;
  const groups = data?.groups || [];
  const maxEvents = Math.max(1, ...groups.map(g => g.events));
  const allUnassigned = groups.length <= 1 && (groups[0]?.group === 'Unassigned');

  return (
    <div data-testid="adoption-teams">
      <div className="flex items-center gap-2 mb-4">
        {['department', 'role'].map(d => (
          <button key={d} onClick={() => setDim(d)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${dim === d ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
            data-testid={`team-dim-${d}`}>{d}</button>
        ))}
      </div>
      {dim === 'department' && allUnassigned && (
        <div className="mb-3 text-[11px] text-amber-400" data-testid="teams-unassigned-note">
          No departments assigned yet. Add a department to users in Administration → Access Control to see per-team adoption.
        </div>
      )}
      <Section title={`Adoption by ${dim}`}>
        <div className="space-y-1" data-testid="team-rollup">
          <div className="flex items-center gap-3 px-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            <div className="flex-1">{dim}</div>
            <div className="w-24 text-right">Active / members</div>
            <div className="w-16 text-right">Adoption</div>
            <div className="w-40">Activity</div>
          </div>
          {groups.map(g => (
            <div key={g.group} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted/30" data-testid={`team-row-${g.group}`}>
              <div className="flex-1 min-w-0 text-sm text-foreground capitalize truncate">{g.group}</div>
              <div className="w-24 text-right text-xs text-muted-foreground">{fmt(g.active_users)} / {fmt(g.members)}</div>
              <div className="w-16 text-right text-xs font-medium text-foreground">{g.adoption_pct}%</div>
              <div className="w-40">
                <div className="h-3 rounded bg-muted/30 overflow-hidden" title={`${fmt(g.events)} events · ${fmt(g.views)} views · ${fmt(g.runs)} runs · ${fmt(g.ai)} AI`}>
                  <div className="h-full bg-primary/60 rounded" style={{ width: `${(g.events / maxEvents) * 100}%` }} />
                </div>
                <div className="text-[9px] text-muted-foreground mt-0.5">{fmt(g.events)} events</div>
              </div>
            </div>
          ))}
          {!groups.length && <Empty text="No teams to show." />}
        </div>
      </Section>
    </div>
  );
}

// ── Users ────────────────────────────────────────────────────────────
function UsersSection({ days }) {
  const [heavy, setHeavy] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [nudging, setNudging] = useState(false);

  useEffect(() => {
    let ok = true;
    setLoading(true);
    Promise.all([api.getHeavyUsers(days), api.getGovernanceUserActivity(days)])
      .then(([h, l]) => { if (ok) { setHeavy(h); setList(l?.users || []); } })
      .catch(() => {})
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [days]);

  if (loading) return <Loading />;

  const filtered = (list || []).filter(u => !q || (u.user_email || '').toLowerCase().includes(q.toLowerCase()));
  const inactive = filtered.filter(u => u.inactive || u.events === 0);

  const runNudge = async (ids) => {
    const targets = (ids || []).filter(Boolean);
    if (!targets.length) { toast.info('No users to nudge.'); return; }
    setNudging(true);
    try {
      const r = await api.nudgeUsers(targets);
      if (!r.email_configured) {
        toast.warning(`Email isn't configured yet — 0 sent. Set up SendGrid to enable onboarding emails.`);
      } else {
        toast.success(`Onboarding email sent to ${r.sent} user${r.sent === 1 ? '' : 's'}${r.failed ? `, ${r.failed} failed` : ''}.`);
      }
    } catch (e) { toast.error(e.message); }
    finally { setNudging(false); }
  };

  return (
    <div data-testid="adoption-users">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Heaviest users">
          <div className="space-y-1" data-testid="heavy-users">
            {(heavy?.most_active || []).map((u, i) => (
              <button key={u.user_id} onClick={() => setSelected(u.user_id)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors text-left" data-testid={`heavy-user-${u.user_id}`}>
                <span className="w-5 text-xs text-muted-foreground">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground truncate">{u.name || u.email || u.user_id}</div>
                  <div className="text-[10px] text-muted-foreground">{u.role || '—'} · active {ago(u.last_active)}</div>
                </div>
                <div className="text-sm font-medium text-foreground">{fmt(u.events)}</div>
              </button>
            ))}
          </div>
        </Section>
        <Section title="Biggest query / AI consumers">
          <div className="space-y-1" data-testid="top-consumers">
            {(heavy?.top_consumers || []).length ? (heavy.top_consumers).map((u, i) => (
              <button key={u.user_id} onClick={() => setSelected(u.user_id)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors text-left" data-testid={`consumer-${u.user_id}`}>
                <Flame className="w-4 h-4 text-orange-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground truncate">{u.name || u.email || u.user_id}</div>
                  <div className="text-[10px] text-muted-foreground">{fmt(u.query_runs)} queries · {fmt(u.ai_invocations)} AI · {fmt(u.exports)} exports</div>
                </div>
                <div className="text-sm font-medium text-foreground">{fmt(u.total)}</div>
              </button>
            )) : <Empty text="No query/AI usage in this window." />}
          </div>
        </Section>
      </div>

      <Section title="All users" right={
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search email…"
          className="bg-muted/30 border border-border/40 rounded-lg px-2 py-1 text-xs text-foreground outline-none focus:border-primary/50" data-testid="user-search" />
      }>
        {inactive.length > 0 && (
          <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid="inactive-note">
            <span className="text-[11px] text-amber-400">
              {fmt(inactive.length)} user{inactive.length === 1 ? '' : 's'} have not been active in the last {days} days — a good onboarding target.
            </span>
            <button onClick={() => runNudge(inactive.map(u => u.user_id))} disabled={nudging}
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/15 text-primary text-[11px] font-medium hover:bg-primary/25 disabled:opacity-50 transition-colors"
              data-testid="nudge-inactive-btn">
              <Mail className="w-3.5 h-3.5" /> {nudging ? 'Sending…' : 'Email inactive users'}
            </button>
          </div>
        )}
        <div className="divide-y divide-border/20 max-h-[420px] overflow-auto" data-testid="user-list">
          {filtered.map(u => (
            <button key={u.user_id} onClick={() => setSelected(u.user_id)} className="w-full flex items-center gap-3 px-2 py-2 hover:bg-muted/30 transition-colors text-left" data-testid={`user-row-${u.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground truncate">{u.user_email || u.user_id}</div>
                <div className="text-[10px] text-muted-foreground">last active {ago(u.last_active)}</div>
              </div>
              {(u.inactive || u.events === 0)
                ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">inactive</span>
                : <span className="text-xs text-foreground">{fmt(u.events)} events</span>}
              <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      </Section>

      {selected && <UserDrawer userId={selected} days={days} onClose={() => setSelected(null)} />}
    </div>
  );
}

function UserDrawer({ userId, days, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.getUserDetail(userId, days).then(x => ok && setD(x)).catch(() => {}).finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [userId, days]);

  return (
    <div className="fixed inset-0 z-[120] flex justify-end" data-testid="user-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-background border-l border-border/50 overflow-auto p-5 shadow-2xl">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40" data-testid="user-drawer-close"><X className="w-4 h-4" /></button>
        {loading ? <Loading /> : !d ? <Empty text="No data." /> : (
          <>
            <div className="mb-4 pr-8">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">User</div>
              <div className="text-lg font-heading font-semibold text-foreground">{d.user.name || d.user.email}</div>
              <div className="text-xs text-muted-foreground">{d.user.email} · {d.user.role || '—'}</div>
              <button
                onClick={async () => {
                  try {
                    const r = await api.nudgeUsers([d.user.user_id]);
                    if (!r.email_configured) toast.warning("Email isn't configured yet — set up SendGrid to enable onboarding emails.");
                    else toast.success(r.sent ? `Onboarding email sent to ${d.user.email}.` : 'Could not send — no email on file.');
                  } catch (e) { toast.error(e.message); }
                }}
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/15 text-primary text-[11px] font-medium hover:bg-primary/25 transition-colors"
                data-testid="user-drawer-nudge">
                <Mail className="w-3.5 h-3.5" /> Send onboarding email
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <Kpi label="Events" value={fmt(d.total_events)} sub={`last ${d.window_days}d`} />
              <Kpi label="Active days" value={fmt(d.active_days)} />
              <Kpi label="Logins" value={fmt(d.login_count)} />
              <Kpi label="Views" value={fmt(d.views)} />
              <Kpi label="Created" value={fmt(d.creates)} />
              <Kpi label="AI / Queries" value={`${fmt(d.ai_invocations)} / ${fmt(d.query_runs)}`} />
            </div>
            <div className="text-[11px] text-muted-foreground mb-4">
              First seen {ago(d.first_active)} · last active {ago(d.last_active)}
            </div>
            <Section title="Top content">
              {d.top_content.length ? d.top_content.map(c => (
                <button key={`${c.object_type}-${c.object_id}`}
                  onClick={() => navigate(c.object_type === 'dashboard' ? `/dashboards/view?id=${c.object_id}` : `/reports/view?id=${c.object_id}`)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/40 text-left">
                  <span className="text-[9px] uppercase text-muted-foreground w-14 shrink-0">{c.object_type}</span>
                  <span className="text-sm text-foreground truncate flex-1">{c.object_name}</span>
                  <span className="text-xs text-muted-foreground">{fmt(c.view_count)}</span>
                </button>
              )) : <Empty text="No content viewed yet." />}
            </Section>
            <Section title="Recent activity">
              <div className="space-y-1">
                {d.recent_activity.length ? d.recent_activity.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="capitalize text-foreground w-16 shrink-0">{(e.event_type || '').replace('_', ' ')}</span>
                    <span className="text-muted-foreground truncate flex-1">{e.object_name || e.object_type || '—'}</span>
                    <span className="text-[10px] text-muted-foreground/70 shrink-0">{ago(e.timestamp)}</span>
                  </div>
                )) : <Empty text="No recent activity." />}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Loading() {
  return <div className="flex items-center gap-2 text-xs text-muted-foreground py-8" data-testid="adoption-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
}
function Empty({ text }) {
  return <div className="text-xs text-muted-foreground border border-dashed border-border/40 rounded-lg p-6 text-center">{text}</div>;
}

export default function AdoptionAnalyticsPage() {
  const [tab, setTab] = useState('overview');
  const [days, setDays] = useState(30);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce(n => n + 1), []);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="adoption-analytics-page">
      <div className="flex items-start gap-3 mb-5 pb-4 border-b border-border/40">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Activity className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Usage & Adoption
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground">Adoption Analytics</h1>
          <p className="text-xs text-muted-foreground mt-1">Who's using the product, what's popular, when it's busy — and where to improve.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            className="bg-muted/30 border border-border/40 rounded-lg px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary/50" data-testid="days-select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
          </select>
          <button onClick={refresh} className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/40 transition-colors" title="Refresh" data-testid="adoption-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-border/30">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`adoption-tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div key={`${tab}-${days}-${nonce}`}>
        {tab === 'overview' && <OverviewSection days={days} />}
        {tab === 'peak' && <PeakSection days={days} />}
        {tab === 'content' && <ContentSection />}
        {tab === 'teams' && <TeamsSection days={days} />}
        {tab === 'users' && <UsersSection days={days} />}
      </div>
    </div>
  );
}
